import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, TrendingUp } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { heroImg, METRICS } from "@/data/content";
import { scrollToId } from "@/lib/scroll";

const container = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15, delayChildren: 0.25 } },
};

const lineReveal = {
  hidden: { y: "112%", rotate: 2.5 },
  visible: { y: "0%", rotate: 0, transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1] } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen overflow-hidden flex items-center pt-24 pb-16">
      <div className="aurora-blob w-[560px] h-[560px] -top-40 -left-40" style={{ background: "radial-gradient(circle, #D4AF37 0%, transparent 65%)" }} />
      <div className="aurora-blob w-[480px] h-[480px] top-1/3 -right-32" style={{ background: "radial-gradient(circle, #06B6D4 0%, transparent 65%)", animationDelay: "-6s" }} />
      <div className="aurora-blob w-[420px] h-[420px] bottom-0 left-1/3" style={{ background: "radial-gradient(circle, #8B5CF6 0%, transparent 65%)", animationDelay: "-11s" }} />
      <div className="absolute inset-0 bg-gradient-to-b from-navy-950/5 via-transparent to-white pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <motion.div
            className="lg:col-span-7"
            variants={container}
            initial="hidden"
            animate="visible"
            style={{ opacity: contentOpacity }}
          >
            <motion.p variants={fadeUp} className="flex items-center gap-3 font-mono-brand text-xs tracking-[0.28em] uppercase text-gold-deep mb-8">
              <span className="h-px w-10 bg-gold/70" />
              {t("hero.eyebrow")}
            </motion.p>

            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[5.2rem] leading-[1.04] tracking-tight text-navy-900">
              <span className="block overflow-hidden pb-1">
                <motion.span variants={lineReveal} className="block">{t("hero.line1")}</motion.span>
              </span>
              <span className="block overflow-hidden pb-2">
                <motion.span variants={lineReveal} className="block italic text-gold-gradient pr-2">
                  {t("hero.line2")}
                </motion.span>
              </span>
            </h1>

            <motion.p variants={fadeUp} className="mt-8 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed">
              {t("hero.sub")}
            </motion.p>

            <motion.div variants={fadeUp} className="mt-10 flex flex-wrap items-center gap-4">
              <button
                data-testid="hero-cta-primary"
                onClick={() => scrollToId("#contact")}
                className="btn-gold inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-semibold"
              >
                {t("hero.cta1")}
                <ArrowRight size={17} strokeWidth={2} />
              </button>
              <button
                data-testid="hero-cta-secondary"
                onClick={() => scrollToId("#services")}
                className="btn-ghost-gold inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-medium"
              >
                {t("hero.cta2")}
              </button>
            </motion.div>

            <motion.div variants={fadeUp} className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl">
              {METRICS.map((m, i) => (
                <div key={i} className={i > 0 ? "sm:border-l sm:border-gold/15 sm:pl-5" : ""}>
                  <p className="font-display text-2xl sm:text-3xl text-gold-deep">{m.prefix}</p>
                  <p className="mt-1 text-xs text-slate-500">{t(m.key)}</p>
                </div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            className="lg:col-span-5 relative"
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div style={{ y: imgY }} className="relative">
              <div className="absolute -inset-3 rounded-[2.2rem] bg-gradient-to-br from-gold/30 via-transparent to-cyan-400/20 blur-xl" />
              <div className="relative rounded-[2rem] overflow-hidden border border-gold/30 shadow-2xl shadow-black/15">
                <img
                  src={heroImg}
                  alt="Coastal metropolis at dusk"
                  className="w-full h-[420px] lg:h-[540px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950/80 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 glass rounded-2xl px-5 py-4 flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-gold/15 text-gold-deep shrink-0">
                    <TrendingUp size={20} strokeWidth={1.75} />
                  </span>
                  <div>
                    <p className="font-display text-xl text-gold-deep">$2.5B+</p>
                    <p className="text-xs text-slate-600">{t("hero.m1")}</p>
                  </div>
                </div>
              </div>
              <div className="animate-floaty absolute -top-6 -right-4 glass rounded-2xl px-5 py-3.5 hidden sm:block">
                <p className="font-display text-xl text-gold-deep">7</p>
                <p className="text-xs text-slate-600">{t("hero.m4")}</p>
              </div>
            </motion.div>
          </motion.div>
        </div>

        <motion.button
          data-testid="hero-scroll-indicator"
          onClick={() => scrollToId("#services")}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="mt-14 lg:mt-8 flex items-center gap-3 text-xs text-slate-500 hover:text-gold-deep transition-colors duration-300"
        >
          <ArrowDown size={14} className="animate-bounce" />
          {t("hero.scroll")}
        </motion.button>
      </div>
    </section>
  );
}
